import type { RootState } from '../types';
import {
  AppointmentFulfillmentType,
  AppointmentLoadType,
  AppointmentScheduleType,
  AppointmentServiceType,
  AppointmentStatusType,
  AppointmentUnloadType,
  ContactType,
  HazardDangerDegreeType,
  ItemUnitOfMeasureType,
  JobTitleType,
  QbECheckStatusType,
  QbChargeStatusType,
  PackingMaterialMadeFromType,
  PackingMaterialType,
  PickAllocationMethodType,
  ReceiptCarrierArrangedByType,
  ReceiptDocumentType,
  ReceiptFulfillmentType,
  ReceiptPackageType,
  ReceiptStatusType,
  SelectOption,
  ShipmentLocationType,
  ShippingCategoryType,
  SystemServiceType,
  WarehouseAppointmentTierType,
  WarehouseAssetType,
  WarehouseLocationAccessibilityType,
  WarehouseLocationType,
  WarehouseStagingAreaType,
  WarehouseStorageCategoryType,
  WarehouseVehicleType,
  WarehouseWorkstationType,
  MoveableUnitType,
  MoveableUnitUsageTypes,
  EmployeeActivityType,
  UserType,
  ReceiptIssueType,
  FeeType,
  MoveableUnitStatusType,
  DeliveryConfirmationType,
  ShipEngineInsuranceProviders,
  CustomsContentsType,
  CustomsNonDeliveryType,
  TaxEntityType,
  TaxIdentifierType,
  FulfilledServiceAssigneeTaskType,
  WarehouseLocationStatusType,
  OrderBatchType,
  ShippingSelectionPreferenceType,
  ReceiptFeeType,
  CreditCardProcessingFeeType,
  DocumentFeeType,
  StorageBillingMethodType,
  QbPaymentTermType,
  InvoiceFrequencyType,
  PaymentMethodType,
  StorageBillingPeriodType,
  FeeCategoryType,
  MinimumCustomerMonthlySpendType,
  ReshipReturnReasonType,
  ReceiptReturnInspectionType,
  ReceiptIssueResolutionType,
  OrderPackingModeType,
  OrderFulfillmentType,
  InventoryItemStatusType,
  OrderStatusType,
  InventoryTrackPreferenceType,
  OrderItemAllocationStatusType,
  ExpressProcessingWorkflowType,
  BoxDimensionCategoryType,
  PalletDimensionCategoryType,
  SoldByTypeType,
  MaterialAccountOwnerType,
  MaterialDimensionCategoryType,
  OrderAllocationStatusType,
} from '../../types';
import {
  InventoryItemTimelineActionType,
  InventoryTransferCreationType,
  InventoryTransferIssueType,
  InventoryTransferStatusType,
} from '../../types/inventoryTypes';

const EMPTY_ARRAY: readonly never[] = [];
const EMPTY_OBJECT: Record<string, never> = {};

export const systemSelectors = {
  machineName: (state: RootState) => state.systemPersist.machineName,
  signalRDevicesReconnecting: (state: RootState) =>
    state.system.signalRDevicesReconnecting,
  signalRDevicesDisconnected: (state: RootState) =>
    state.system.signalRDevicesDisconnected,
  signalRDevicesSubscribedToScale: (state: RootState) =>
    state.system.signalRDevicesSubscribedToScale,
  signalRMessagesConnected: (state: RootState) =>
    state.system.signalRMessagesConnected,
  signalRDevicesConnected: (state: RootState) =>
    state.system.signalRDevicesConnected,
  signalRAlertsConnected: (state: RootState) =>
    state.system.signalRAlertsConnected,
  scaleWeight: (state: RootState) => state.system.weight || EMPTY_OBJECT,
  assignedServices: (state: RootState) => state.system.assignedServices,
  lastFetchedAssignedServices: (state: RootState) =>
    state.system.lastFetchedAssignedServices,
  openOrderStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.openOrderStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<OrderStatusType>>,
  closedOrderStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.closedOrderStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<OrderStatusType>>,
  orderStatus: (state: RootState) =>
    (state.systemPersist.systemSettings.orderStatus || EMPTY_ARRAY) as Array<
      SelectOption<OrderStatusType>
    >,
  orderItemAllocationStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.orderItemAllocationStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<OrderItemAllocationStatusType>>,
  fulfillmentTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.fulfillmentTypes ||
      EMPTY_ARRAY) as Array<SelectOption<OrderFulfillmentType>>,
  orderBatchTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.orderBatchTypes ||
      EMPTY_ARRAY) as Array<SelectOption<OrderBatchType>>,
  titleTypes: (state: RootState) =>
    state.systemPersist.systemSettings.titleTypes || EMPTY_ARRAY,
  states: (state: RootState) =>
    (state.systemPersist.systemSettings.states || EMPTY_ARRAY) as Array<
      SelectOption<string | number>
    >,
  countries: (state: RootState) =>
    (state.systemPersist.systemSettings.countries || EMPTY_ARRAY) as Array<
      SelectOption<string | number>
    >,
  statesObj: (state: RootState) =>
    state.systemPersist.systemSettings.statesObj || EMPTY_OBJECT,
  countriesObj: (state: RootState) =>
    state.systemPersist.systemSettings.countriesObj || EMPTY_OBJECT,
  countryUsId: (state: RootState) => state.systemPersist.countryUsId as number,
  packingMaterialTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.packingMaterialTypes ||
      EMPTY_ARRAY) as Array<SelectOption<PackingMaterialType>>,
  materialMadeFromTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.materialMadeFromTypes ||
      EMPTY_ARRAY) as Array<SelectOption<PackingMaterialMadeFromType>>,
  packingModeTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.packingModeTypes ||
      EMPTY_ARRAY) as Array<SelectOption<OrderPackingModeType>>,
  hazardClassTypes: (state: RootState) =>
    state.systemPersist.systemSettings.hazardClassTypes || EMPTY_ARRAY,
  unitOfMeasureTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.unitOfMeasureTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ItemUnitOfMeasureType>>,
  materialAccountOwnerTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.materialAccountOwnerTypes ||
      EMPTY_ARRAY) as Array<SelectOption<MaterialAccountOwnerType>>,
  carrierTypes: (state: RootState) =>
    state.systemPersist.systemSettings.carrierTypes || EMPTY_ARRAY,
  carrierCodesTypes: (state: RootState) =>
    state.systemPersist.systemSettings.carrierCodesTypes || EMPTY_ARRAY,
  carrierServiceCategoryTypes: (state: RootState) =>
    state.systemPersist.systemSettings.carrierServiceCategoryTypes ||
    EMPTY_ARRAY,
  freightContainerPackingTypes: (state: RootState) =>
    state.systemPersist.systemSettings.freightContainerPackingTypes ||
    EMPTY_ARRAY,
  shippingCategoryTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.shippingCategoryTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ShippingCategoryType>>,
  shipmentLocationTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.shipmentLocationTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ShipmentLocationType>>,
  deliveryConfirmationTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.deliveryConfirmationTypes ||
      EMPTY_ARRAY) as Array<SelectOption<DeliveryConfirmationType>>,
  orderShipmentTypes: (state: RootState) =>
    state.systemPersist.systemSettings.orderShipmentTypes || EMPTY_ARRAY,
  shippingFeeTypes: (state: RootState) =>
    state.systemPersist.systemSettings.shippingFeeTypes || EMPTY_ARRAY,
  trackingStatusTypes: (state: RootState) =>
    state.systemPersist.systemSettings.trackingStatusTypes || EMPTY_ARRAY,
  customerFeeTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.customerFeeTypes ||
      EMPTY_ARRAY) as Array<SelectOption<FeeType>>,
  specialFeeTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.specialFeeTypes ||
      EMPTY_ARRAY) as Array<SelectOption<FeeType>>,
  documentFeeTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.documentFeeTypes ||
      EMPTY_ARRAY) as Array<SelectOption<DocumentFeeType>>,
  receiptDocumentTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptDocumentTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptDocumentType>>,
  feeCategoryTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.feeCategoryTypes ||
      EMPTY_ARRAY) as Array<SelectOption<FeeCategoryType>>,
  feeTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.feeTypes || EMPTY_ARRAY) as Array<
      SelectOption<FeeType>
    >,
  receiptFeeTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptFeeTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptFeeType>>,
  profitAndLossFeeTypeFilters: (state: RootState) =>
    state.systemPersist.systemSettings.profitAndLossFeeTypeFilters ||
    EMPTY_ARRAY,
  materialCategoryTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.materialCategoryTypes ||
      EMPTY_ARRAY) as Array<SelectOption<MaterialDimensionCategoryType>>,
  palletDimensionCategoryTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.palletDimensionCategoryTypes ||
      EMPTY_ARRAY) as Array<SelectOption<PalletDimensionCategoryType>>,
  boxDimensionCategoryTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.boxDimensionCategoryTypes ||
      EMPTY_ARRAY) as Array<SelectOption<BoxDimensionCategoryType>>,
  soldByTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.soldByTypes || EMPTY_ARRAY) as Array<
      SelectOption<SoldByTypeType>
    >,
  jobTitleTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.jobTitleTypes || EMPTY_ARRAY) as Array<
      SelectOption<JobTitleType>
    >,
  contactTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.contactTypes || EMPTY_ARRAY) as Array<
      SelectOption<ContactType>
    >,
  roleTypes: (state: RootState) =>
    state.systemPersist.systemSettings.roles || EMPTY_ARRAY,
  userTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.userTypes || EMPTY_ARRAY) as Array<
      SelectOption<UserType>
    >,
  fulfilledServiceAssigneeTaskTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.fulfilledServiceAssigneeTaskTypes ||
      EMPTY_ARRAY) as Array<SelectOption<FulfilledServiceAssigneeTaskType>>,
  minimumCustomerMonthlySpendTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.minimumCustomerMonthlySpendTypes ||
      EMPTY_ARRAY) as Array<SelectOption<MinimumCustomerMonthlySpendType>>,
  paymentMethodTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.paymentMethodTypes ||
      EMPTY_ARRAY) as Array<SelectOption<PaymentMethodType>>,
  paymentTermTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.paymentTermTypes ||
      EMPTY_ARRAY) as Array<SelectOption<QbPaymentTermType>>,
  invoiceFrequencyTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.invoiceFrequencyTypes ||
      EMPTY_ARRAY) as Array<SelectOption<InvoiceFrequencyType>>,
  shippingSelectionPreferenceTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.shippingSelectionPreferenceTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ShippingSelectionPreferenceType>>,
  shipEngineInsuranceProviderTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.shipEngineInsuranceProviderTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ShipEngineInsuranceProviders>>,
  carrierAccountOwnerTypes: (state: RootState) =>
    state.systemPersist.systemSettings.carrierAccountOwnerTypes || EMPTY_ARRAY,
  shipApiTypes: (state: RootState) =>
    state.systemPersist.systemSettings.shipApiTypes || EMPTY_ARRAY,
  customerShipmentLabelTypes: (state: RootState) =>
    state.systemPersist.systemSettings.customerShipmentLabelTypes ||
    EMPTY_ARRAY,
  batchTemplates: (state: RootState) =>
    state.system.batchTemplates || EMPTY_ARRAY,
  customsContentsType: (state: RootState) =>
    (state.systemPersist.internationalShippingTypes.customsContentsType ||
      EMPTY_ARRAY) as Array<SelectOption<CustomsContentsType>>,
  customsNonDeliveryType: (state: RootState) =>
    (state.systemPersist.internationalShippingTypes.customsNonDeliveryType ||
      EMPTY_ARRAY) as Array<SelectOption<CustomsNonDeliveryType>>,
  taxEntityType: (state: RootState) =>
    (state.systemPersist.internationalShippingTypes.taxEntityType ||
      EMPTY_ARRAY) as Array<SelectOption<TaxEntityType>>,
  taxIdentifierType: (state: RootState) =>
    (state.systemPersist.internationalShippingTypes.taxIdentifierType ||
      EMPTY_ARRAY) as Array<SelectOption<TaxIdentifierType>>,
  reshipReturnReasonTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.reshipReturnReasonTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReshipReturnReasonType>>,
  shipEngineReturnChargeEventTypes: (state: RootState) =>
    state.systemPersist.systemSettings.shipEngineReturnChargeEventTypes ||
    EMPTY_ARRAY,
  openShipmentJobStatus: (state: RootState) =>
    state.systemPersist.systemSettings.openShipmentJobStatusTypes ||
    EMPTY_ARRAY,
  scheduledShipmentJobStatus: (state: RootState) =>
    state.systemPersist.systemSettings.scheduledShipmentJobStatusTypes ||
    EMPTY_ARRAY,
  reportTypes: (state: RootState) =>
    state.systemPersist.systemSettings.reportTypes || EMPTY_ARRAY,
  openDrayageLoadStatusTypes: (state: RootState) =>
    state.systemPersist.systemSettings.openDrayageLoadStatusTypes ||
    EMPTY_ARRAY,
  completedDrayageLoadStatusTypes: (state: RootState) =>
    state.systemPersist.systemSettings.completedDrayageLoadStatusTypes ||
    EMPTY_ARRAY,
  drayageLoadStatusTypes: (state: RootState) =>
    state.systemPersist.systemSettings.drayageLoadStatusTypes || EMPTY_ARRAY,
  cardTypes: (state: RootState) =>
    state.systemPersist.systemSettings.cardTypes || EMPTY_ARRAY,
  creditCardProcessingFeeTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.creditCardProcessingFeeTypes ||
      EMPTY_ARRAY) as Array<SelectOption<CreditCardProcessingFeeType>>,
  invoiceStatusTypes: (state: RootState) =>
    state.systemPersist.systemSettings.invoiceStatusTypes || EMPTY_ARRAY,
  invoiceTypes: (state: RootState) =>
    state.systemPersist.systemSettings.invoiceTypes || EMPTY_ARRAY,
  billStatusTypes: (state: RootState) =>
    state.systemPersist.systemSettings.billStatusTypes || EMPTY_ARRAY,
  reconciliationTypes: (state: RootState) =>
    state.systemPersist.systemSettings.reconciliationTypes || EMPTY_ARRAY,
  basicQbVendors: (state: RootState) =>
    state.system.basicQbVendors || EMPTY_ARRAY,
  basicQbTerms: (state: RootState) => state.system.basicQbTerms || EMPTY_ARRAY,
  basicQbClasses: (state: RootState) =>
    state.system.basicQbClasses || EMPTY_ARRAY,
  basicQbItems: (state: RootState) => state.system.basicQbItems || EMPTY_ARRAY,
  systemServiceTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.systemServiceTypes ||
      EMPTY_ARRAY) as Array<SelectOption<SystemServiceType>>,
  employeeActivityTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.employeeActivityTypes ||
      EMPTY_ARRAY) as Array<SelectOption<EmployeeActivityType>>,
  warehouseClosedDayTypes: (state: RootState) =>
    state.systemPersist.systemSettings.warehouseClosedDayTypes || EMPTY_ARRAY,
  receiptPackageTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptPackageTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptPackageType>>,
  receiptStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptStatusType>>,
  receiptFulfillmentTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptFulfillmentTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptFulfillmentType>>,
  receiptCarrierArrangedByTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptCarrierArrangedByTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptCarrierArrangedByType>>,
  receiptReturnInspectionTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptReturnInspectionTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptReturnInspectionType>>,
  pickAllocationMethodTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.pickAllocationMethodTypes ||
      EMPTY_ARRAY) as Array<SelectOption<PickAllocationMethodType>>,
  inventoryTrackPreferenceTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.inventoryTrackPreferenceTypes ||
      EMPTY_ARRAY) as Array<SelectOption<InventoryTrackPreferenceType>>,
  appointmentStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.appointmentStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<AppointmentStatusType>>,
  appointmentScheduleTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.appointmentScheduleTypes ||
      EMPTY_ARRAY) as Array<SelectOption<AppointmentScheduleType>>,
  appointmentServiceTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.appointmentServiceTypes ||
      EMPTY_ARRAY) as Array<SelectOption<AppointmentServiceType>>,
  appointmentFulfillmentTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.appointmentFulfillmentTypes ||
      EMPTY_ARRAY) as Array<SelectOption<AppointmentFulfillmentType>>,
  appointmentLoadTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.appointmentLoadTypes ||
      EMPTY_ARRAY) as Array<SelectOption<AppointmentLoadType>>,
  appointmentUnloadTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.appointmentUnloadTypes ||
      EMPTY_ARRAY) as Array<SelectOption<AppointmentUnloadType>>,
  warehouseAppointmentTierTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseAppointmentTierTypes ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseAppointmentTierType>>,
  warehouseLocationTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseLocationTypes ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseLocationType>>,
  warehouseStorageCategoryTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseStorageCategoryTypes ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseStorageCategoryType>>,
  storageBillingMethodTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.storageBillingMethodTypes ||
      EMPTY_ARRAY) as Array<SelectOption<StorageBillingMethodType>>,
  storageBillingPeriodTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.storageBillingPeriodTypes ||
      EMPTY_ARRAY) as Array<SelectOption<StorageBillingPeriodType>>,
  warehouseLocationStatusType: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseLocationStatusType ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseLocationStatusType>>,
  warehouseStagingAreaTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseStagingAreaTypes ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseStagingAreaType>>,
  warehouseLocationAccessibilityTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseLocationAccessibilityTypes ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseLocationAccessibilityType>>,
  warehouseAssetTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseAssetTypes ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseAssetType>>,
  warehouseWorkstationTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseWorkstationTypes ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseWorkstationType>>,
  warehouseVehicleTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.warehouseVehicleTypes ||
      EMPTY_ARRAY) as Array<SelectOption<WarehouseVehicleType>>,
  qbChargeStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.qbChargeStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<QbChargeStatusType>>,
  qbECheckStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.qbECheckStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<QbECheckStatusType>>,
  hazardDangerDegreeTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.hazardDangerDegreeTypes ||
      EMPTY_ARRAY) as Array<SelectOption<HazardDangerDegreeType>>,
  moveableUnitTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.moveableUnitTypes ||
      EMPTY_ARRAY) as Array<SelectOption<MoveableUnitType>>,
  moveableUnitStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.moveableUnitStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<MoveableUnitStatusType>>,
  moveableUnitUsageTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.moveableUnitUsageTypes ||
      EMPTY_ARRAY) as Array<SelectOption<MoveableUnitUsageTypes>>,
  receiptIssueTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptIssueTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptIssueType>>,
  receiptIssueResolutionTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.receiptIssueResolutionTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ReceiptIssueResolutionType>>,
  inventoryItemTimelineActionTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.inventoryItemTimelineActionTypes ||
      EMPTY_ARRAY) as Array<SelectOption<InventoryItemTimelineActionType>>,
  inventoryItemStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.inventoryItemStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<InventoryItemStatusType>>,
  inventoryTransferCreationTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.inventoryTransferCreationTypes ||
      EMPTY_ARRAY) as Array<SelectOption<InventoryTransferCreationType>>,
  inventoryTransferStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.inventoryTransferStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<InventoryTransferStatusType>>,
  inventoryTransferIssueTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.inventoryTransferIssueTypes ||
      EMPTY_ARRAY) as Array<SelectOption<InventoryTransferIssueType>>,
  expressProcessingWorkflowTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.expressProcessingWorkflowTypes ||
      EMPTY_ARRAY) as Array<SelectOption<ExpressProcessingWorkflowType>>,
  orderAllocationStatusTypes: (state: RootState) =>
    (state.systemPersist.systemSettings.orderAllocationStatusTypes ||
      EMPTY_ARRAY) as Array<SelectOption<OrderAllocationStatusType>>,
};

export const resourceQueryParams = (resource: string) => (state: RootState) =>
  state.lists.resources[resource]?.params.queryParams;
